import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Page, PageBody, PageLoader } from '@customerio/pluma-components/react';
import { DocumentTitle } from '@/components/page/document-title';
import { SafeNavigate } from '@/services/router/safe-navigate';
import { getIntegrationsQueryOptions } from '@/services/db/integration/queries';
import { useDataOutIntegration } from '@/services/db/data-out-integration/queries';
import { DataOutConnectorHeader } from './components/data-out-connector-header';
import { DataOutConnectorForm } from './components/data-out-connector-form';
import { LoadErrorBanner } from './components/load-error-banner';
import { useDataOutBreadcrumbs, useIntegrationsIndexHref } from './hooks';

type DataOutConnectorPageProps = {
  type: string;
  /** The workspace `DataOutRouteView` resolved. */
  workspaceId: string;
};

/**
 * The `/integrations/data_out/:type` connector config page.
 *
 * Two reads, both of which Ember's `model()` hook awaited before rendering: the catalog entry (for
 * the connector's name, blurb, logo, and — load-bearing — its settings schema) and the saved record.
 * The form is generated from that schema and seeded from that record, so both are gated rather than
 * rendered against empty data: a control resolving a pick against a catalog that hasn't arrived
 * would store the wrong value and never self-heal.
 */
export default function DataOutConnectorPage({ type, workspaceId }: DataOutConnectorPageProps) {
  const qc = useQueryClient();
  // Keyed off the resolved workspace, not the URL: this route renders under `/workspaces/last`
  // before the resolver rewrites it, and reading the id from the URL there throws
  // "No workspace id in URL." into the error boundary.
  const workspace = Number(workspaceId);

  const catalog = useQuery(getIntegrationsQueryOptions(workspace, qc));
  const dataOut = useDataOutIntegration({ identifier: type, workspaceId: workspace });

  useDataOutBreadcrumbs(workspaceId, type);
  const integrationsIndexHref = useIntegrationsIndexHref(workspaceId);

  if (catalog.isPending || dataOut.isPending) {
    return <PageLoader label="Loading integration…" />;
  }

  // A catalog that failed to load has no entries to match against, which is indistinguishable from
  // an unknown type further down — so it is handled first. Otherwise a transient outage would bounce
  // the user to the directory as though the connector did not exist.
  if (catalog.isError) {
    return (
      <Page>
        <PageBody>
          <LoadErrorBanner />
        </PageBody>
      </Page>
    );
  }

  const integration = (catalog.data ?? []).find((candidate) => candidate.id === type);

  // An unrecognised connector type. Ember rendered an empty shell here (its `find` returned
  // undefined and the template read through it); sending the user back to the directory is the
  // intentional deviation.
  if (integration == null) {
    return <SafeNavigate to={integrationsIndexHref} options={{ shouldReplaceHistoryEntry: true }} />;
  }

  return (
    <>
      <DocumentTitle title={integration.name} />
      <Page>
        <PageBody>
          <DataOutConnectorHeader integration={integration} />
          <DataOutConnectorForm
            integration={integration}
            dataOut={dataOut.data ?? null}
            workspaceId={String(workspaceId)}
            loadError={dataOut.error}
          />
        </PageBody>
      </Page>
    </>
  );
}
